import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "../../hooks";
import { isPackAvailableForMode, isOwnedPackAllowedForPatient, isOwnedPackAvailableForDuration, } from "./utils/sessionPacks";
export function PatientSelectionStep({ state, dispatch, showSessionPacks = true, selectedDuration = null, }) {
    const { t } = useTranslation("appointment");
    const { verifiedPatients, activeSessionPacks, patientTab, selectedPatient, selectedActiveSessionPack, newPatientForm, selectedDoctor, patientsLoading, consultationMode, } = state;
    const hasExistingPatients = verifiedPatients.length > 0;
    const [searchQuery, setSearchQuery] = useState("");
    const toIdArray = (val) => {
        if (Array.isArray(val))
            return val;
        if (typeof val === "number")
            return [val];
        return [];
    };
    const isPackForDoctor = (pack) => {
        if (!selectedDoctor)
            return true;
        const ids = toIdArray(pack.allowedDoctorIds);
        return ids.length === 0 || ids.includes(selectedDoctor.id);
    };
    const availablePacks = activeSessionPacks.filter((pack) => pack.sessionsRemaining > 0 &&
        isPackForDoctor(pack) &&
        isPackAvailableForMode(pack, consultationMode) &&
        isOwnedPackAvailableForDuration(pack, selectedDuration) &&
        isOwnedPackAllowedForPatient(pack, patientTab, selectedPatient));
    if (patientsLoading) {
        return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", sx: { py: 5 }, children: [_jsx(CircularProgress, { size: 20 }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1.5 }, children: t("loadingPatients") })] }));
    }
    return (_jsx(Box, { sx: {
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: { xs: 2.5, sm: "28px 24px" },
        }, children: _jsxs(Box, { sx: { maxWidth: 560, mx: "auto" }, children: [hasExistingPatients && (_jsxs(Tabs, { value: patientTab, onChange: (_, val) => dispatch({ type: "SET_PATIENT_TAB", payload: val }), sx: { mb: 2, minHeight: 36 }, children: [_jsx(Tab, { label: t("existingPatients"), value: "existing", sx: { minHeight: 36, textTransform: "none", fontSize: 13 } }), _jsx(Tab, { label: t("addNew"), value: "new", sx: { minHeight: 36, textTransform: "none", fontSize: 13 } })] })), patientTab === "existing" && hasExistingPatients ? (_jsxs(_Fragment, { children: [_jsx(TextField, { size: "small", placeholder: t("searchPatientsPlaceholder"), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), fullWidth: true, sx: { mb: 1 }, slotProps: {
                                input: {
                                    startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { sx: { fontSize: 18, color: "text.secondary" } }) })),
                                },
                            } }), _jsx(Stack, { spacing: 1, sx: { maxHeight: 200, overflowY: "auto" }, children: verifiedPatients
                                .filter((p) => {
                                const fullName = [p.firstName, p.lastName]
                                    .filter(Boolean)
                                    .join(" ")
                                    .toLowerCase();
                                return fullName.includes(searchQuery.toLowerCase());
                            })
                                .map((patient) => (_jsx(PatientCard, { patient: patient, selected: selectedPatient?.id === patient.id, onSelect: () => dispatch({
                                    type: "SET_SELECTED_PATIENT",
                                    payload: patient,
                                }), t: t }, patient.id))) })] })) : (_jsx(NewPatientFormSection, { form: newPatientForm, dispatch: dispatch, t: t })), showSessionPacks && availablePacks.length > 0 && (_jsxs(Box, { sx: { mt: 2.5 }, children: [_jsx(Typography, { variant: "overline", color: "text.secondary", sx: { display: "block", mb: 1, letterSpacing: 0.5 }, children: t("activeSessionPacks") }), _jsx(Stack, { spacing: 0.75, children: availablePacks.map((pack) => {
                                return (_jsx(SessionPackCard, { pack: pack, selected: selectedActiveSessionPack?.id === pack.id, onSelect: () => {
                                        const next = selectedActiveSessionPack?.id === pack.id ? null : pack;
                                        dispatch({
                                            type: "SET_SELECTED_ACTIVE_SESSION_PACK",
                                            payload: next,
                                        });
                                    }, t: t }, pack.id));
                            }) }), selectedActiveSessionPack && (_jsx(Alert, { severity: "success", sx: { mt: 1, py: 0, fontSize: 12 }, children: t("sessionPackApplied") }))] }))] }) }));
}
function PatientCard({ patient, selected, onSelect, t, }) {
    const fullName = [patient.firstName, patient.lastName]
        .filter(Boolean)
        .join(" ");
    const details = [
        patient.gender,
        patient.age ? `${patient.age}y` : null,
        patient.mrn ? `MRN: ${patient.mrn}` : null,
    ]
        .filter(Boolean)
        .join(" · ");
    return (_jsxs(Button, { onClick: onSelect, variant: "outlined", fullWidth: true, sx: {
            justifyContent: "flex-start",
            textAlign: "left",
            textTransform: "none",
            px: 1.5,
            py: 1,
            gap: 1,
            borderColor: selected ? "primary.main" : "divider",
            bgcolor: selected ? "primary.light" : "background.paper",
            "&:hover": { bgcolor: selected ? "primary.light" : "action.hover" },
        }, children: [_jsx(Radio, { checked: selected, size: "small", sx: { p: 0 } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: {
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, children: fullName || t("unknownPatient") }), details && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: details }))] })] }));
}
function NewPatientFormSection({ form, dispatch, t, }) {
    const update = (field, value) => {
        dispatch({ type: "SET_NEW_PATIENT_FORM", payload: { [field]: value } });
    };
    return (_jsxs(Stack, { spacing: 1.5, children: [_jsxs(Grid, { container: true, spacing: 1.25, children: [_jsx(Grid, { size: { xs: 6 }, children: _jsx(TextField, { label: t("fullNameLabel"), size: "small", fullWidth: true, value: form.fullName, onChange: (e) => update("fullName", e.target.value), placeholder: t("fullNamePlaceholder") }) }), _jsx(Grid, { size: { xs: 6 }, children: _jsxs(TextField, { label: t("genderLabel"), size: "small", fullWidth: true, select: true, value: form.gender, onChange: (e) => update("gender", e.target.value), children: [_jsx(MenuItem, { value: "", children: t("select") }), _jsx(MenuItem, { value: "MALE", children: t("male") }), _jsx(MenuItem, { value: "FEMALE", children: t("female") }), _jsx(MenuItem, { value: "OTHER", children: t("other") })] }) })] }), _jsxs(Grid, { container: true, spacing: 1.25, children: [_jsx(Grid, { size: { xs: 6 }, children: _jsx(TextField, { label: t("cityLabel"), size: "small", fullWidth: true, value: form.city, onChange: (e) => update("city", e.target.value), placeholder: t("cityPlaceholder") }) }), _jsx(Grid, { size: { xs: 6 }, children: _jsx(TextField, { label: t("ageLabel"), size: "small", fullWidth: true, type: "number", value: form.age, onChange: (e) => update("age", e.target.value), placeholder: t("agePlaceholder") }) })] }), _jsx(TextField, { label: t("emailLabel"), size: "small", fullWidth: true, type: "email", value: form.email, onChange: (e) => update("email", e.target.value), placeholder: t("emailPlaceholder") }), _jsx(TextField, { label: t("addressLabel"), size: "small", fullWidth: true, value: form.address, onChange: (e) => update("address", e.target.value), placeholder: t("addressPlaceholder") })] }));
}
function SessionPackCard({ pack, selected, onSelect, t, }) {
    return (_jsxs(Button, { onClick: onSelect, variant: "outlined", fullWidth: true, sx: {
            justifyContent: "flex-start",
            textAlign: "left",
            textTransform: "none",
            px: 1.5,
            py: 1,
            gap: 1,
            borderColor: selected ? "success.main" : "divider",
            bgcolor: selected ? "success.light" : "background.paper",
            "&:hover": {
                bgcolor: selected ? "success.light" : "action.hover",
            },
        }, children: [_jsx(Checkbox, { checked: selected, size: "small", color: "success", sx: { p: 0 } }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600, color: "text.primary" }, children: pack.sessionPackName }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("sessionsRemaining", undefined, {
                            remaining: pack.sessionsRemaining,
                            total: pack.totalSessions,
                        }) })] }), _jsx(Chip, { label: t("freeChip"), size: "small", color: "success", sx: { fontWeight: 600, fontSize: 11 } })] }));
}
